import { FC, useCallback, useMemo } from 'react';
import { usePermission, useTranslation } from 'hooks';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { RootState } from 'store';
import landingState from 'constants/landingState';
import Messages from 'constants/rpcTypes';
import { createSelector } from 'reselect';
import './header.scss';
import { ComponentRegistry } from 'runtime/registry/components/ComponentRegistry';
import { headerRegistry } from './registry';
import { HeaderItemId, HeaderLayout } from './types';

export const defaultHeaderLayout: HeaderLayout = {
  left: ['SidebarToggle', 'Timezone', 'Help', 'LiveLogo'],
  center: ['Logo'],
  right: ['ChangeLang', 'HelpDesk', 'Notifications', 'Search', 'UserProfile'],
};

const selectUserStates = createSelector(
  [(state: RootState) => state.user?.landingState],
  (userLandingState) => ({
    isDemoStage: userLandingState === landingState.demoStage,
    isNotMaf: userLandingState !== landingState.maf,
  })
);

export const Header: FC = (props) => {
  const Component = ComponentRegistry.getComponent('Header');

  if (Component) {
    return <Component {...props} />;
  }
  const getTranslate = useTranslation();
  const { isEnabled } = usePermission();
  const { isDemoStage, isNotMaf } = useSelector(selectUserStates);
  const menuVersion = useSelector(
    (state: RootState) => state.layoutConfiguration.menuVersion
  );
  const logoUrl = useSelector(
    (state: RootState) => state.settings.brandConfig.config.assets.logoUrl
  );
  const isHelpdeskAvailable =
    isEnabled(Messages.SupportChat_Messages) ||
    isEnabled(Messages.HelpTips_List);

  const configProps = ComponentRegistry.getProps('Header');
  const resolvedLayout = ComponentRegistry.getResolvedLayout(
    'Header',
    defaultHeaderLayout
  );

  const renderItem = useCallback(
    (itemId: HeaderItemId) => {
      const Component = headerRegistry[itemId];

      if (!Component) {
        return null;
      }

      const props = useMemo(
        () => ({
          isHelpdeskAvailable,
          getTranslate,
          isDemoStage,
          isNotMaf,
          isEnabled,
          logoUrl,
        }),
        [
          isHelpdeskAvailable,
          getTranslate,
          isDemoStage,
          isNotMaf,
          isEnabled,
          logoUrl,
        ]
      );

      const itemProps = configProps?.[itemId] || {};
      const mergedProps = {
        ...props,
        ...(itemProps as Partial<typeof props>),
      };

      return (
        <Component key={itemId} {...mergedProps} className='header__item' />
      );
    },
    [configProps]
  );

  return (
    <div className='header'>
      <div
        className={classNames('header__inner', {
          'header__inner_old-menu': menuVersion === 'old',
        })}>
        <div className='header__left'>
          {resolvedLayout.left.map(renderItem)}
        </div>

        <div className='header__center'>
          {resolvedLayout.center.map(renderItem)}
        </div>

        <div className='header__right'>
          {resolvedLayout.right.map(renderItem)}
        </div>
      </div>
    </div>
  );
};
